class DuckPromoGridCardElement extends HTMLElement {
  constructor() {
    super();

    this.handleResize = this.handleResize.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleCardClick = this.handleCardClick.bind(this);
    this.handleHotspotClick = this.handleHotspotClick.bind(this);
    this.handleHotspotKeyDown = this.handleHotspotKeyDown.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.hotspot = this.querySelector('[data-duck-promo-hotspot]');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.finePointerQuery = window.matchMedia('(pointer: fine)');
    this.resetSpotlightPosition();
    this.setDuckRevealed(false);

    this.addEventListener('pointerenter', this.handlePointerEnter);
    this.addEventListener('pointermove', this.handlePointerMove);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('click', this.handleCardClick);

    if (this.hotspot) {
      this.hotspot.addEventListener('click', this.handleHotspotClick);
      this.hotspot.addEventListener('keydown', this.handleHotspotKeyDown);
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.handleResize);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.handlePointerEnter);
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('click', this.handleCardClick);

    if (this.hotspot) {
      this.hotspot.removeEventListener('click', this.handleHotspotClick);
      this.hotspot.removeEventListener('keydown', this.handleHotspotKeyDown);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.handleResize);
    }

    window.clearTimeout(this.navigationTimeout);
    window.clearTimeout(this.sparkTimeout);
    window.cancelAnimationFrame(this.resizeFrame);
  }

  get supportsFinePointer() {
    return this.finePointerQuery.matches;
  }

  setSpotlightActive(active) {
    this.dataset.spotlightActive = active ? 'true' : 'false';
  }

  handleResize() {
    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resetSpotlightPosition();
      this.updateRevealState();
    });
  }

  getPercentageValue(variableName, fallback) {
    const value = parseFloat(window.getComputedStyle(this).getPropertyValue(variableName));
    return Number.isFinite(value) ? value : fallback;
  }

  setDuckRevealed(active) {
    this.dataset.duckRevealed = active ? 'true' : 'false';
  }

  setSpotlightPositionFromClient(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    this.style.setProperty('--duck-promo-grid-card-spotlight-x', `${Math.max(0, Math.min(100, x)).toFixed(2)}%`);
    this.style.setProperty('--duck-promo-grid-card-spotlight-y', `${Math.max(0, Math.min(100, y)).toFixed(2)}%`);
    this.updateRevealState();
  }

  resetSpotlightPosition() {
    const styles = window.getComputedStyle(this);
    const hotspotX = styles.getPropertyValue('--duck-promo-grid-card-hotspot-x').trim() || '50%';
    const hotspotY = styles.getPropertyValue('--duck-promo-grid-card-hotspot-y').trim() || '50%';

    this.style.setProperty('--duck-promo-grid-card-spotlight-x', hotspotX);
    this.style.setProperty('--duck-promo-grid-card-spotlight-y', hotspotY);
  }

  updateRevealState() {
    if (this.dataset.spotlightActive !== 'true') {
      this.setDuckRevealed(false);
      return;
    }

    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      this.setDuckRevealed(false);
      return;
    }

    const spotlightX = (this.getPercentageValue('--duck-promo-grid-card-spotlight-x', 50) / 100) * rect.width;
    const spotlightY = (this.getPercentageValue('--duck-promo-grid-card-spotlight-y', 50) / 100) * rect.height;
    const duckX = (this.getPercentageValue('--duck-promo-grid-card-duck-x', 50) / 100) * rect.width;
    const duckY = (this.getPercentageValue('--duck-promo-grid-card-duck-y', 52) / 100) * rect.height;
    const spotlightRadiusX = (this.getPercentageValue('--duck-promo-grid-card-spotlight-radius-x', 18) / 100) * rect.width;
    const spotlightRadiusY = (this.getPercentageValue('--duck-promo-grid-card-spotlight-radius-y', 20) / 100) * rect.height;
    const normalizedDistance = Math.hypot(
      (spotlightX - duckX) / Math.max(spotlightRadiusX, 1),
      (spotlightY - duckY) / Math.max(spotlightRadiusY, 1),
    );

    this.setDuckRevealed(normalizedDistance <= 0.42);
  }

  triggerSpark() {
    if (!this.hotspot) return;

    this.hotspot.dataset.sparkActive = 'false';
    window.requestAnimationFrame(() => {
      if (this.hotspot) {
        this.hotspot.dataset.sparkActive = 'true';
      }
    });

    window.clearTimeout(this.sparkTimeout);
    this.sparkTimeout = window.setTimeout(() => {
      if (this.hotspot) {
        this.hotspot.dataset.sparkActive = 'false';
      }
    }, this.prefersReducedMotion.matches ? 220 : 720);
  }

  handlePointerEnter(event) {
    if (!this.supportsFinePointer) return;

    this.setSpotlightActive(true);
    this.setSpotlightPositionFromClient(event.clientX, event.clientY);
  }

  handlePointerMove(event) {
    if (!this.supportsFinePointer) return;
    if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;

    this.setSpotlightActive(true);
    this.setSpotlightPositionFromClient(event.clientX, event.clientY);
  }

  handlePointerLeave(event) {
    if (!this.supportsFinePointer) return;
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;

    this.setSpotlightActive(false);
    this.setDuckRevealed(false);
    this.resetSpotlightPosition();
  }

  handleFocusIn() {
    this.setSpotlightActive(true);
    this.resetSpotlightPosition();
    this.updateRevealState();
  }

  handleFocusOut(event) {
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;

    this.setSpotlightActive(false);
    this.setDuckRevealed(false);
    this.resetSpotlightPosition();
  }

  handleCardClick(event) {
    if (!this.hotspot) return;
    if (event.target === this.hotspot || this.hotspot.contains(event.target)) return;

    this.triggerSpark();
  }

  handleHotspotClick(event) {
    const targetUrl = event.currentTarget.dataset.targetUrl || '';

    this.triggerSpark();

    if (!targetUrl) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    window.clearTimeout(this.navigationTimeout);

    if (this.prefersReducedMotion.matches) {
      window.location.assign(targetUrl);
      return;
    }

    this.navigationTimeout = window.setTimeout(() => {
      window.location.assign(targetUrl);
    }, 180);
  }

  handleHotspotKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.setSpotlightActive(false);
      this.setDuckRevealed(false);
      this.resetSpotlightPosition();
      event.currentTarget.blur();
    }
  }
}

if (!customElements.get('duck-promo-grid-card')) {
  customElements.define('duck-promo-grid-card', DuckPromoGridCardElement);
}
